import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, type KeywordDetail, type KeywordAssetView } from "../../lib/api.js";

/**
 * Regenerate an already-published article "from the start", with operator gates.
 *
 * Reuses the existing per-stage endpoints (refresh / brief-meta / produce / images /
 * publish) rather than a monolithic backend call, so every step stays reviewable.
 * The publish step preserves the ORIGINAL publish date (keyword.firstPublishedAt →
 * schema.org datePublished) while advancing dateModified (= lastReviewedAt), so a
 * re-publish signals freshness without telling Google the article is brand-new.
 *
 * Trigger is manual, per-article — for when a piece drops in ranking or has new
 * content potential.
 */
export function RegenerateModal({
  keywordId,
  detail,
  onClose,
  onAfterAction,
}: {
  keywordId: string;
  detail: KeywordDetail;
  onClose: () => void;
  onAfterAction: () => void;
}) {
  const qc = useQueryClient();
  const k = detail.keyword;
  const article = k.article as { generatedAt?: string } | null;

  const fmt = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-[61] flex w-full max-w-2xl flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-line p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <RefreshCw className="h-4 w-4 text-brand" /> Regenerate article
            </div>
            <div className="mt-1 truncate text-xs text-muted" dir="auto">{k.keyword}</div>
          </div>
          <button onClick={onClose} className="btn-ghost shrink-0" title="Close (Esc)">✕</button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Freshness snapshot */}
          <div className="rounded-lg border border-line bg-canvas p-3 text-xs">
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Original publish" value={fmt(k.firstPublishedAt)} hint="schema.org datePublished — stays fixed across regenerations" />
              <Stat label="Last reviewed" value={fmt(k.lastReviewedAt ?? article?.generatedAt)} hint="drives dateModified — advances on refresh" />
              <Stat label="Produced" value={fmt(article?.generatedAt)} hint="when the current body was generated" />
            </div>
            {!k.firstPublishedAt && (
              <div className="mt-2 flex items-start gap-1.5 text-2xs text-muted">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warn" />
                No stored original publish date yet — it's captured/back-filled from the live doc the next time you publish, then preserved.
              </div>
            )}
          </div>

          <StepSerp keywordId={keywordId} onAfterAction={onAfterAction} />
          <StepContent keywordId={keywordId} onAfterAction={onAfterAction} />
          <StepImages keywordId={keywordId} />
          <StepPublish keywordId={keywordId} onAfterAction={onAfterAction} qc={qc} />
        </div>
      </aside>
    </>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div title={hint}>
      <div className="text-2xs uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 font-medium text-ink">{value}</div>
    </div>
  );
}

function StepShell({ n, title, subtitle, children }: { n: number; title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-line p-3">
      <div className="mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/10 text-2xs font-bold text-brand">{n}</span>
          {title}
        </div>
        <div className="mt-0.5 pl-7 text-2xs text-muted">{subtitle}</div>
      </div>
      <div className="pl-7">{children}</div>
    </section>
  );
}

// ── Step 1: SERP re-check ────────────────────────────────────────────────────
function StepSerp({ keywordId, onAfterAction }: { keywordId: string; onAfterAction: () => void }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.refreshArticle(keywordId);
      setResult({ ok: r.ok, text: r.summary ?? (r.ok ? "Refreshed" : r.error ?? "Refresh failed") });
      if (r.ok) onAfterAction();
    } catch (e) {
      setResult({ ok: false, text: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };
  return (
    <StepShell n={1} title="Re-check the SERP" subtitle="Diff the live SERP vs the stored snapshot and stamp a fresh review date. This is what advances dateModified.">
      <button className="btn-secondary text-xs" onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-check SERP
      </button>
      {result && <Note ok={result.ok}>{result.text}</Note>}
    </StepShell>
  );
}

// ── Step 2: Content (meta + body) ────────────────────────────────────────────
function StepContent({ keywordId, onAfterAction }: { keywordId: string; onAfterAction: () => void }) {
  const qc = useQueryClient();
  const [metaLoading, setMetaLoading] = useState(false);
  const [meta, setMeta] = useState<{ ok: boolean; text: string } | null>(null);

  const regenMeta = async () => {
    setMetaLoading(true);
    setMeta(null);
    try {
      const r = await api.regenerateBriefMeta(keywordId);
      setMeta({ ok: r.ok, text: r.ok ? `H1: ${r.meta.outlineH1}  ·  Title: ${r.meta.metaTitle}` : "Meta regeneration failed" });
      if (r.ok) onAfterAction();
    } catch (e) {
      setMeta({ ok: false, text: (e as Error).message });
    } finally {
      setMetaLoading(false);
    }
  };

  // Body regen — reuse the produce SSE + polled job (survives refresh/reopen).
  const produceStatus = useQuery({
    queryKey: ["produce-status", keywordId],
    queryFn: () => api.getProduceStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "producing" ? 2000 : false),
  });
  const job = produceStatus.data;
  const producing = job?.status === "producing";

  const regenBody = () => {
    const ok = window.confirm(
      "Regenerate the full article body from the current brief? This runs the section calls again (costs tokens) and replaces the existing body. The original publish date is preserved on the next publish.",
    );
    if (!ok) return;
    api.produceArticle(keywordId, { withMeta: true }, {
      onDone: () => { onAfterAction(); qc.invalidateQueries({ queryKey: ["produce-status", keywordId] }); },
      onError: () => qc.invalidateQueries({ queryKey: ["produce-status", keywordId] }),
    });
    setTimeout(() => qc.invalidateQueries({ queryKey: ["produce-status", keywordId] }), 300);
  };

  return (
    <StepShell n={2} title="Regenerate content" subtitle="Refresh the SERP-grounded H1 + meta, and optionally re-write the body. Both optional — skip what you don't need.">
      <div className="flex flex-wrap gap-2">
        <button className="btn-secondary text-xs" onClick={regenMeta} disabled={metaLoading}>
          {metaLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate H1 + meta
        </button>
        <button className="btn-secondary text-xs" onClick={regenBody} disabled={producing}>
          {producing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate body
        </button>
      </div>
      {meta && <Note ok={meta.ok}>{meta.text}</Note>}
      {job && job.status !== "idle" && (
        <div className="mt-2 text-2xs text-muted">
          {producing
            ? `Producing… ${job.done ?? 0}/${job.total ?? "?"}${job.currentHeading ? ` · ${job.currentHeading}` : ""}`
            : job.status === "done"
            ? `Body regenerated · ${job.summary?.totalWordCount ?? "?"}w · $${job.summary?.estimatedCostUsd?.toFixed(4) ?? "?"}`
            : job.status === "error"
            ? `Body regen failed: ${job.error ?? ""}`
            : null}
        </div>
      )}
    </StepShell>
  );
}

// ── Step 3: Images (keep / regenerate all / selective) ───────────────────────
function StepImages({ keywordId }: { keywordId: string }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"keep" | "replace">("keep");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const assetsQuery = useQuery({
    queryKey: ["pipeline", "keyword", keywordId, "assets"],
    queryFn: () => api.listKeywordAssets(keywordId),
  });
  const imagesStatus = useQuery({
    queryKey: ["images-status", keywordId],
    queryFn: () => api.getImagesStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "generating" ? 2500 : false),
  });
  const job = imagesStatus.data;
  const generating = job?.status === "generating";

  const assets = assetsQuery.data?.assets ?? [];
  const imageAssets = useMemo(
    () => assets.filter((a) => ["hero", "in_content", "social_card", "thumbnail"].includes(a.kind)),
    [assets],
  );

  const toggle = (id: number) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const bodyFromSelection = (picked: KeywordAssetView[]) => ({
    mode: "selective" as const,
    hero: picked.some((a) => a.kind === "hero"),
    social: picked.some((a) => a.kind === "social_card"),
    thumbnail: picked.some((a) => a.kind === "thumbnail"),
    sections: picked.filter((a) => a.kind === "in_content" && a.sectionIndex != null).map((a) => a.sectionIndex as number),
  });

  const startPoll = () => setTimeout(() => qc.invalidateQueries({ queryKey: ["images-status", keywordId] }), 300);

  const regenAll = async () => {
    if (!window.confirm("Regenerate ALL images? This costs image-generation credits and overwrites every image.")) return;
    await api.regenerateImages(keywordId, { mode: "all" });
    startPoll();
  };
  const regenSelected = async () => {
    const picked = imageAssets.filter((a) => selected.has(a.id));
    if (picked.length === 0) return;
    await api.regenerateImages(keywordId, bodyFromSelection(picked));
    startPoll();
  };

  // When a run finishes, refresh assets + article (markdown may have re-slugged).
  if (job?.status === "done" && assetsQuery.isStale) qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId, "assets"] });

  return (
    <StepShell n={3} title="Images" subtitle="Keep the current images (zero cost), regenerate everything, or replace just the ones you pick.">
      <div className="flex gap-3 text-xs">
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === "keep"} onChange={() => setMode("keep")} /> Keep all
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} /> Replace some
        </label>
      </div>

      {mode === "keep" && (
        <div className="mt-2 text-2xs text-muted">The existing {imageAssets.length} image(s) are reused as-is when you re-publish. Nothing to do here.</div>
      )}

      {mode === "replace" && (
        <div className="mt-2">
          <div className="mb-2 flex flex-wrap gap-2">
            <button className="btn-secondary text-xs" onClick={regenSelected} disabled={generating || selected.size === 0}>
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate selected ({selected.size})
            </button>
            <button className="btn-ghost text-xs" onClick={regenAll} disabled={generating}>Regenerate all</button>
          </div>
          {assetsQuery.isPending ? (
            <div className="text-2xs text-muted">Loading assets…</div>
          ) : imageAssets.length === 0 ? (
            <div className="text-2xs text-muted">No image assets on this article yet.</div>
          ) : (
            <ul className="grid grid-cols-2 gap-2">
              {imageAssets.map((a) => (
                <li key={a.id}>
                  <label className={`flex items-center gap-2 rounded-md border p-2 text-2xs ${selected.has(a.id) ? "border-brand bg-brand/5" : "border-line"}`}>
                    <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} disabled={generating} />
                    {a.sanityUrl ? (
                      <img src={a.sanityUrl} alt="" className="h-8 w-8 rounded object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded bg-line" />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium capitalize">{a.kind.replace("_", " ")}{a.sectionIndex != null ? ` · §${a.sectionIndex}` : ""}</div>
                      <div className="truncate text-muted">{a.status}</div>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {job && job.status !== "idle" && (
        <div className="mt-2 text-2xs text-muted">
          {generating
            ? "Generating images…"
            : job.status === "done"
            ? `Images done · ${job.summary?.generated ?? 0} generated, ${job.summary?.skipped ?? 0} skipped`
            : job.status === "error"
            ? `Image regen failed: ${job.error ?? ""}`
            : null}
        </div>
      )}
    </StepShell>
  );
}

// ── Step 4: Re-publish (with preserved date) ─────────────────────────────────
function StepPublish({ keywordId, onAfterAction, qc }: { keywordId: string; onAfterAction: () => void; qc: ReturnType<typeof useQueryClient> }) {
  const [loading, setLoading] = useState<"preview" | "live" | null>(null);
  const [preview, setPreview] = useState<{ resolvedPublishedAt?: string | null; wasFirstPublish?: boolean; imagesResolved?: number; imagesUnresolved?: number; warnings?: string[]; writeMode?: "patch" | "createOrReplace" } | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("en-GB") : "—");

  const runPreview = async () => {
    setLoading("preview");
    setResult(null);
    setPreview(null);
    try {
      const r = await api.publishToSanity(keywordId, { publish: true, dryRun: true });
      if (!r.ok) throw new Error(r.error ?? "Preview failed");
      setPreview(r);
    } catch (e) {
      setResult({ ok: false, text: (e as Error).message });
    } finally {
      setLoading(null);
    }
  };

  const runLive = async () => {
    if (!window.confirm("Publish live to Sanity now? This overwrites the live blog-posts document (regenerated body/meta/images) while preserving the original publish date.")) return;
    setLoading("live");
    setResult(null);
    try {
      const r = await api.publishToSanity(keywordId, { publish: true });
      setResult({ ok: r.ok, text: r.ok ? (r.summary ?? "Published") : (r.error ?? "Publish failed") });
      if (r.ok) { onAfterAction(); qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId] }); }
    } catch (e) {
      setResult({ ok: false, text: (e as Error).message });
    } finally {
      setLoading(null);
    }
  };

  return (
    <StepShell n={4} title="Re-publish" subtitle="Push the regenerated article to the same Sanity doc. datePublished stays on the original date; dateModified advances.">
      <div className="flex flex-wrap gap-2">
        <button className="btn-secondary text-xs" onClick={runPreview} disabled={loading !== null}>
          {loading === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Preview (dry-run)
        </button>
        <button className="btn-primary text-xs" onClick={runLive} disabled={loading !== null}>
          {loading === "live" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Publish live
        </button>
      </div>

      {preview && (
        <div className="mt-2 rounded-md border border-line bg-canvas p-2.5 text-2xs">
          <div className="mb-1 flex items-center gap-1.5 font-medium text-ink"><CheckCircle2 className="h-3.5 w-3.5 text-accent" /> Dry-run preview — nothing written</div>
          <div>datePublished (preserved): <span className="font-medium">{fmt(preview.resolvedPublishedAt)}</span>{preview.wasFirstPublish ? " (would be a first publish — stamped now)" : ""}</div>
          <div>Write mode: <span className="font-medium">{preview.writeMode === "patch" ? "patch — updates content, keeps your Studio edits (categories/tags/main image)" : "create/replace — full write (first publish)"}</span></div>
          <div>Images: {preview.imagesResolved ?? 0} resolved{preview.imagesUnresolved ? `, ${preview.imagesUnresolved} unresolved` : ""}</div>
          {preview.warnings && preview.warnings.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-muted">
              {preview.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
      {result && <Note ok={result.ok}>{result.text}</Note>}
    </StepShell>
  );
}

function Note({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <div className={`mt-2 rounded-md px-2.5 py-1.5 text-2xs ${ok ? "bg-accent/10 text-accent" : "bg-danger/10 text-danger"}`} dir="auto">
      {children}
    </div>
  );
}
